import { l10n } from '@ott/l10n';
import { VirtualCardPassenger } from '@ott/shared-virtual-card-logic';
import { NON_NUMBER_PAT } from '@ott/transforms';
// @ts-ignore
import rules from '@ott/utility-form-validate-rules';
import { applyPattern } from '@ott/utility-formatters';

import { InnRequest } from '../types';
import { FORM_FIELDS, INN_FIELDS, NAME_REGEXP, NUMBER_REGEXP } from './constants';

import * as yup from 'yup';
import { AnyObject } from 'yup/lib/types';

const requiredMessage = l10n('virtualCard.upgradeForm.messages.required');

const testDate = (value = '', { createError, path }: yup.TestContext<AnyObject>) => {
  if (!value) {
    return createError({
      message: requiredMessage,
      path,
    });
  }

  const isValid = rules.validateBirthDate(value);

  if (!isValid) {
    return createError({
      message: l10n('virtualCard.upgradeForm.messages.invalidFormat'),
      path,
    });
  }

  return true;
};

export const formDataScheme = yup
  .object({
    [FORM_FIELDS.FIRST_NAME]: yup
      .string()
      .required(requiredMessage)
      .matches(NAME_REGEXP, { message: l10n('virtualCard.upgradeForm.messages.incorrectLocale') }),
    [FORM_FIELDS.LAST_NAME]: yup
      .string()
      .required(requiredMessage)
      .matches(NAME_REGEXP, { message: l10n('virtualCard.upgradeForm.messages.incorrectLocale') }),
    [FORM_FIELDS.MIDDLE_NAME]: yup.string().matches(NAME_REGEXP, {
      message: l10n('virtualCard.upgradeForm.messages.incorrectLocale'),
      excludeEmptyString: true,
    }),
    [FORM_FIELDS.BIRTH_DATE]: yup.string().required(requiredMessage).test(testDate),
    [FORM_FIELDS.DOCUMENT_NUMBER]: yup
      .string()
      .required(requiredMessage)
      .matches(NUMBER_REGEXP, { message: l10n('virtualCard.upgradeForm.messages.invalidFormat') }),
    [FORM_FIELDS.DOCUMENT_ISSUE_DATE]: yup.string().required(requiredMessage).test(testDate),
    [FORM_FIELDS.DOCUMENT_UNIT_CODE]: yup
      .string()
      .required(requiredMessage)
      .min(6, l10n('virtualCard.upgradeForm.messages.invalidFormat'))
      .max(6, l10n('virtualCard.upgradeForm.messages.invalidFormat')),
    [FORM_FIELDS.INN]: yup.string().required(requiredMessage),
    [FORM_FIELDS.TAX_OK]: yup.boolean(),
  })
  .required();

export const formDataRequestScheme = formDataScheme.shape({
  isTaxResidentOfRussianFed: yup.boolean().required(),
  isTaxResidentOfOtherCountries: yup.boolean().required(),
  wasBornInUnitedStates: yup.boolean().required(),
});

export const isInnReady = (params: InnRequest) => {
  return INN_FIELDS.every((key) => Boolean(params[key])) && params.documentNumber.length === 10;
};

export function documentUnitCodeFormatter(
  value: string,
  prevValue: string,
  cursor: number,
  props: { isFocused: boolean }
) {
  return applyPattern(value, cursor, NON_NUMBER_PAT, () => '111-111');
}

export function innFormatter(
  value: string,
  prevValue: string,
  cursor: number,
  props: { isFocused: boolean }
) {
  return applyPattern(value, cursor, NON_NUMBER_PAT, () => '111111111111');
}

export function snilsFormatter(
  value: string,
  prevValue: string,
  cursor: number,
  props: { isFocused: boolean }
) {
  return applyPattern(value, cursor, NON_NUMBER_PAT, () => '111-111-111 11');
}

const formateBirthDate = (birthDate: string, isDisplay = true) => {
  const date = `${birthDate.slice(-2)}`;
  const month = `${birthDate.slice(4, 6)}`;
  const year = `${birthDate.slice(0, 4)}`;

  return isDisplay ? `${date}.${month}.${year}` : `${date}${month}${year}`;
};

export const getOptionsFromPassangers = (passengers: VirtualCardPassenger[]) => {
  const onlyRuLocalePassenages =
    passengers?.filter(({ locales: { ru }, documentCountry, documentType }) => {
      const isRussianDoc = documentCountry === 'RU';
      const isRusPassport = documentType === 'PASSPORT';

      return Boolean(ru) && isRussianDoc && isRusPassport;
    }) || [];

  const options = onlyRuLocalePassenages.map(
    ({
      birthDate,
      documentNumber,
      locales: { ru: { firstName = '', lastName = '', middleName = '' } = {} },
    }) => {
      const formatedBirthDate = formateBirthDate(birthDate);
      const formatedDocumentNumber = `${documentNumber.slice(0, 4)} ${documentNumber.slice(4)}`;
      const fullName = `${lastName} ${firstName} ${middleName}`;

      return {
        label: lastName,
        value: documentNumber,
        birthDate: formatedBirthDate,
        documentNumber: formatedDocumentNumber,
        fullName,
      };
    }
  );

  return options;
};

export const getPassengerInfo = (passenger: VirtualCardPassenger) => {
  const {
    locales: { ru: { firstName = '', lastName = '', middleName = '' } = {} },
    documentNumber,
    birthDate,
  } = passenger;

  const formatedBirthDate = formateBirthDate(birthDate, false);

  return {
    firstName,
    lastName,
    middleName,
    documentNumber,
    birthDate: formatedBirthDate,
  };
};
